import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { ionMic, ionSendSharp } from '@ng-icons/ionicons';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';
import { SseClient } from 'ngx-sse-client';
import { BehaviorSubject, debounce, Observable, Subscription } from 'rxjs';
import { JwtService } from '../services/jwt.service';
import { AnimatedBackgroundComponent } from '../animated-background/animated-background.component';
import { AnimationManagerComponent } from '../animation-manager/animation-manager.component';
import { HalComponent } from '../hal/hal.component';
import { DeepgramAgentService } from '../services/deepgramAgent.service';
import { VoiceBotService, VoiceBotStatus } from '../services/voice-bot.service';
import { stsConfig } from '../common/constants';
import { AudioUtils } from '../common/audio-utils';
import { ChatMessage } from '../chatbot/model_ai_chat';
import { environment } from '../../environments/environment';
import { getApiAuthHeaders } from '../services/session-auth.store';
import { MicrophoneService } from '../services/microphone.service';
import { RealtimeVoiceAgentComponent } from '../realtime_agent/realtime-voice-agent.component';
import { HalRealtimeComponent } from '../hal/hal-realtime.component';
import { WakeLockService } from '../services/wake-lock.service';

@Component({
  selector: 'app-chatbot-agent',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    RouterModule,
    NgxEchartsDirective,
    AnimatedBackgroundComponent,
    AnimationManagerComponent,
    HalComponent,
    RealtimeVoiceAgentComponent,
    HalRealtimeComponent
  ],
  viewProviders: [provideIcons({ ionSendSharp, ionMic })],
  templateUrl: './chatbot_agent.component.html',
  styleUrl: './chatbot_agent.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [NO_ERRORS_SCHEMA],
  providers: [provideEcharts()]
})
export class ChatbotAgentComponent implements OnInit, AfterViewInit, OnDestroy {
  private apiUrl = environment.apiUrl;

  private readonly internalSub = new Subscription();
  isMobileEnv = false;

  @Input() cif: string | null = null;
  @Input() currentSessionId: string | null = null;
  @Input() currentUserName: string | null = null;
  //@Output() cancelChatbot = new EventEmitter<void>();

  agentVoiceAnalyser!: AnalyserNode;
  userVoiceAnalyser!: AnalyserNode;
  agentVolume: number = 0;
  userVolume: number = 0;

  connectionStatus$ = new BehaviorSubject<string>('Disconnected');
  lastMessage: string = 'No messages received yet.';

  private agentAudioContext: AudioContext | null = null;
  private microphoneStream: MediaStream | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;

  private scheduledAudioSources: AudioBufferSourceNode[] = [];
  private startTimeRef = { current: 0 };
  private analyzer!: AnalyserNode;

  private subscriptions: Subscription[] = [];
  private hiddenCloseTimer: number | null = null;

  agentStatus$: Observable<VoiceBotStatus>;
  private isMutedSubject = new BehaviorSubject<boolean>(false);
  isMuted$ = this.isMutedSubject.asObservable();

  showStartSpeaking$ = new BehaviorSubject(false);

  captionWords: string[] = [];
  captionLeaving = false;
  captionOverflowing = false;
  @ViewChild('captionBox') private captionBox?: ElementRef<HTMLElement>;
  @ViewChild('captionText') private captionText?: ElementRef<HTMLElement>;
  private captionTarget: string[] = [];
  private captionUpdatedAt = 0;
  private captionRevealedAt = 0;
  private captionTimer: number | null = null;
  private captionFadeTimer: number | null = null;

  isAndroidPlatform = false;
  // Temperory History Variable
  tempHistory: ChatMessage[] = [];
  @Output() cancelChatbot = new EventEmitter<ChatMessage[]>();
  // Inject HttpClient in the constructor
  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    public deviceService: DeviceDetectorService,
    private sseClient: SseClient,
    private renderer: Renderer2,
    private el: ElementRef,
    private jwtService: JwtService,
    private deepgramAgentWebSocketService: DeepgramAgentService,
    private voiceBotService: VoiceBotService,
    private audioUtils: AudioUtils,
    private microphoneService: MicrophoneService,
    private wakeLockService: WakeLockService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.agentStatus$ = this.voiceBotService.getStatus();
    this.isAndroidPlatform = this.deviceService?.os?.toLowerCase() === 'android';
  }

  async ngOnInit(): Promise<void> {
    if (this.isAndroidPlatform) {
      document.documentElement.classList.add('gfh-android');
    }

    /*this.route.queryParams.subscribe(params => {
      this.cif = params['cif'];
      this.currentSessionId = params['currentSessionId'];
      console.log('Received CIF Number:', this.cif);
      console.log('Received Session ID:', this.currentSessionId);
    });*/

    this.wakeLockService.enable();

    // Close voice mode only after the app stays hidden (avoid iOS mic-permission flicker).
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    console.log('Received CIF Number:', this.cif);
    console.log('Received Session ID:', this.currentSessionId);
    console.log('Received User Name:', this.currentUserName);

    // We use the Agent Connection from HAL Component
    //this.deepgramAgentWebSocketService.connect();

    this.subscriptions.push(
      this.deepgramAgentWebSocketService.socketOpen$.subscribe(() => {
        this.connectionStatus$.next('Connected');
        this.voiceBotService.setStatus(VoiceBotStatus.SLEEPING);
        this.sendDGMessage(JSON.stringify(stsConfig));
        this.showStartSpeaking$.next(true);

        this.startDeepgramAgentStreaming(false);
      })
    );

    this.subscriptions.push(
      this.deepgramAgentWebSocketService.socketMessage$.subscribe(
        async message => {
          if (message instanceof Blob) {
            // Convert Blob to ArrayBuffer
            const arrayBuffer = await message.arrayBuffer();
            if (arrayBuffer && arrayBuffer.byteLength > 0) {
              console.log('Received Binary Message (Converted to ArrayBuffer)');
              this.playAudio(arrayBuffer);
            }
          } else if (typeof message === 'string') {
            // To Determine the Message Coming from Deepgram
            try {
              const data = JSON.parse(message);
              if (data.type === 'ConversationText') {
                this.showStartSpeaking$.next(false);
                this.storeMessage(data);
              } else if (data.type === 'UserStartedSpeaking') {
                // User Started Speaking So If Agent is Speaking then We need to Stop That
                this.clearAudio();
              } else if (data.type === 'FunctionCallRequest') {
                console.log('Received Message:', message);

                const functionCallId = data.function_call_id;
                const inputQuestion = data.input.question;
                const functionName = data.function_name;

                console.log('Function input:', inputQuestion);

                if (functionName === 'ask_question') {
                  // Make the API call manually
                  const response = await fetch(`${this.apiUrl}/ask_question`, {
                    method: 'POST',
                    headers: getApiAuthHeaders({
                      'Content-Type': 'application/json',
                      'X-GFH-AI-CHATBOT-CIF': this.cif,
                      'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId
                    }),
                    body: JSON.stringify({
                      question: inputQuestion,
                      session_name: 'default'
                    })
                  });

                  const result = await response.json();
                  console.log('Function Call Response:', result);

                  // Send response back to Deepgram
                  this.deepgramAgentWebSocketService.sendMessage(
                    JSON.stringify({
                      type: 'FunctionCallResponse',
                      function_call_id: functionCallId,
                      //output: result.response
                      output: result.answer
                    })
                  );
                }
              } else {
                console.log('Received Message:', message);
                this.lastMessage = message;
              }
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          }
        }
      )
    );

    this.subscriptions.push(
      this.deepgramAgentWebSocketService.socketClose$.subscribe(() => {
        this.connectionStatus$.next('Disconnected');
      })
    );

    this.subscriptions.push(
      this.deepgramAgentWebSocketService.socketError$.subscribe(error => {
        console.error('WebSocket Error:', error);
      })
    );
  }

  async ngAfterViewInit(): Promise<void> {
    console.info('Browser Info:', JSON.stringify(this.deviceService));

    if (this.deviceService?.deviceType?.toLowerCase() == 'mobile') {
      this.isMobileEnv = true;
    }
  }

  async handleOrbClick() {
    console.log('Orb clicked!');
    this.voiceBotService.toggleSleep();
    this.startDeepgramAgentStreaming(false);
  }

  sendDGMessage(message: string) {
    this.deepgramAgentWebSocketService.sendMessage(message);
  }

  disconnect() {
    this.deepgramAgentWebSocketService.disconnect();
  }

  ngOnDestroy() {
    document.documentElement.classList.remove('gfh-android');
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.hiddenCloseTimer !== null) {
      window.clearTimeout(this.hiddenCloseTimer);
      this.hiddenCloseTimer = null;
    }
    this.internalSub.unsubscribe();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.wakeLockService.disable();
    this.stopCaptionTimer();
    if (this.captionFadeTimer !== null) {
      window.clearTimeout(this.captionFadeTimer);
      this.captionFadeTimer = null;
    }
  }

  onAgentCaption(text: string): void {
    const target = text.split(/\s+/).filter(Boolean);
    this.captionTarget = target;
    this.captionUpdatedAt = performance.now();

    if (!target.length) {
      this.fadeOutCaption();
      return;
    }
    if (this.captionLeaving) {
      return;
    }

    let common = 0;
    while (common < this.captionWords.length && this.captionWords[common] === target[common]) {
      common++;
    }
    if (common === 0 && this.captionWords.length > 0) {
      this.fadeOutCaption();
      return;
    }
    if (common < this.captionWords.length) {
      this.captionWords = this.captionWords.slice(0, common);
    }
    this.startCaptionTimer();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private fadeOutCaption(): void {
    this.stopCaptionTimer();
    if (!this.captionWords.length) {
      this.resetCaption();
      return;
    }
    if (this.captionLeaving) {
      return;
    }
    this.captionLeaving = true;
    this.cdr.markForCheck();
    this.captionFadeTimer = window.setTimeout(() => {
      this.captionFadeTimer = null;
      this.resetCaption();
      this.cdr.detectChanges();
    }, 350);
  }

  private resetCaption(): void {
    this.captionLeaving = false;
    this.captionWords = [];
    this.captionOverflowing = false;
    if (this.captionTarget.length) {
      this.startCaptionTimer();
    }
  }

  private startCaptionTimer(): void {
    if (this.captionTimer !== null) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.captionTimer = window.setInterval(this.tickCaption, 40);
    });
  }

  private stopCaptionTimer(): void {
    if (this.captionTimer !== null) {
      window.clearInterval(this.captionTimer);
      this.captionTimer = null;
    }
  }

  /** Reveals one word at a time; the trailing word waits until deltas pause, since it may still be partial. */
  private tickCaption = (): void => {
    if (this.captionLeaving) {
      return;
    }
    const now = performance.now();
    const settled = now - this.captionUpdatedAt > 350;
    const available = settled ? this.captionTarget.length : Math.max(0, this.captionTarget.length - 1);
    const shown = this.captionWords.length;

    if (shown >= available) {
      if (settled) {
        this.stopCaptionTimer();
      }
      return;
    }

    const backlog = available - shown;
    const delay = backlog > 12 ? 45 : backlog > 5 ? 90 : 150;
    if (now - this.captionRevealedAt < delay) {
      return;
    }

    this.captionRevealedAt = now;
    this.captionWords = [...this.captionWords, this.captionTarget[shown]];
    this.cdr.detectChanges();

    const box = this.captionBox?.nativeElement;
    const textEl = this.captionText?.nativeElement;
    const overflowing = !!box && !!textEl && textEl.offsetHeight > box.clientHeight + 1;
    if (overflowing !== this.captionOverflowing) {
      this.captionOverflowing = overflowing;
      this.cdr.detectChanges();
    }
  };

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.hiddenCloseTimer = window.setTimeout(() => {
        if (document.hidden) {
          console.log('Voice agent closed after app stayed in background');
          this.closeConversationalAgent();
        }
      }, 3000);
      return;
    }

    if (this.hiddenCloseTimer !== null) {
      window.clearTimeout(this.hiddenCloseTimer);
      this.hiddenCloseTimer = null;
    }
  };

  // Deepgram Websocket
  private cleanupResources() {
    if (this.audioWorkletNode) {
      if (this.audioWorkletNode?.port) {
        this.audioWorkletNode.port.close();
      }

      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    if (this.microphoneStream) {
      this.microphoneStream.getTracks().forEach(track => track.stop());
      this.microphoneStream = null;
    }
    if (this.agentAudioContext) {
      this.agentAudioContext.close();
      this.agentAudioContext = null;
    }
  }

  /**
   * Start Transcribinging from Microphone click
   */
  async startDeepgramAgentStreaming(isMobileMedia) {
    this.cleanupResources();

    if (!isMobileMedia) {
      // This is with the Audio Context When we are using Desktop like devices

      // Configuration for audio capture
      const audioConstraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true
        },
        video: false
      };

      console.log('Checking if mic has been allowed.');
      // Create the AudioContext with the correct sample rate
      // this.audioContext = new AudioContext({ sampleRate: 16000, latencyHint: "interactive" });
      this.agentAudioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 16000,
        latencyHint: 'interactive'
      });

      console.log('Mic check is complete???.');

      // Resume the audio context to avoid browser restrictions
      if (this.agentAudioContext.state === 'suspended') {
        await this.agentAudioContext.resume();
      }
      // Load the worklet processor
      await this.agentAudioContext.audioWorklet.addModule(
        'assets/pcm-worklet-processor.js'
      );

      // Create the worklet node
      this.audioWorkletNode = new AudioWorkletNode(
        this.agentAudioContext,
        'pcm-processor'
      );

      // Connect the worklet node to the audio context
      this.microphoneStream = await navigator.mediaDevices.getUserMedia(
        audioConstraints
      );
      const source = this.agentAudioContext.createMediaStreamSource(
        this.microphoneStream
      );
      source.connect(this.audioWorkletNode);

      // Handle PCM data from the worklet processor
      this.audioWorkletNode.port.onmessage = event => {
        const pcmData = event.data;

        // Sending audio data to deepgram
        this.deepgramAgentWebSocketService.sendAudioChunk(pcmData);

        //const dataArray = new Uint8Array(pcmData);
      };
    } else {
      // This is for mobile devices and browsers like safari
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(stream => {
            try {
              const mediaRecorder = new MediaRecorder(stream);

              mediaRecorder.ondataavailable = event => {
                if (event.data.size > 0) {
                  event.data
                    .arrayBuffer()
                    .then(arrayBuffer => {
                      // Send the ArrayBuffer to the WebSocket
                      if (!this.isMuted$) {
                        // If Microphone is Unmute then Only Send the Audio
                        this.deepgramAgentWebSocketService.sendAudioChunk(
                          this.convertToPCM(arrayBuffer)
                        );
                      }
                    })
                    .catch(error => {
                      console.error(
                        'Error converting Blob to ArrayBuffer:',
                        error
                      );
                    });
                }
              };

              mediaRecorder.onstart = () => console.log('Recording started');
              mediaRecorder.onerror = error =>
                console.error('MediaRecorder error:', error);

              // Start recording (adjust interval as necessary)
              mediaRecorder.start(100);
            } catch (e) {
              console.error('MediaRecorder not supported or failed:', e);
            }
          })
          .catch(error => {
            console.error('Error accessing microphone:', error);
            alert('Microphone access is required to use this feature.');
          });
      } else {
        console.error('get_UserMedia not supported on this browser.');
        alert('Your browser does not support audio recording.');
      }
    }
  }

  convertToPCM(audioData) {
    const buffer = new ArrayBuffer(audioData.length * 2); // 16-bit PCM
    const view = new DataView(buffer);

    for (let i = 0; i < audioData.length; i++) {
      let sample = Math.max(-1, Math.min(1, audioData[i])); // Clamp between -1 and 1
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff; // Convert to 16-bit PCM
      view.setInt16(i * 2, sample, true); // Little-endian
    }

    return buffer;
  }

  playAudio(data) {
    const audioBuffer = this.audioUtils.createAudioBuffer(
      this.agentAudioContext,
      data
    );
    if (!audioBuffer) return;

    const source = this.audioUtils.playAudioBuffer(
      this.agentAudioContext,
      audioBuffer,
      this.startTimeRef,
      this.analyzer
    );
    this.scheduledAudioSources.push(source);
  }

  clearAudio() {
    this.scheduledAudioSources.forEach(source => source.stop());
    this.scheduledAudioSources = [];
  }

  shouldShowIdleMessage(): boolean {
    let agentStatus = '';
    let connectionStatus = '';

    this.agentStatus$.subscribe(status => (agentStatus = status));
    this.connectionStatus$.subscribe(status => (connectionStatus = status));

    return (
      agentStatus === VoiceBotStatus.SLEEPING &&
      connectionStatus === 'Connected'
    );
  }

  /*toggleMicrophoneMute() {
    const newMuteState = !this.isMutedSubject.getValue();
    this.microphoneStream.getAudioTracks().forEach(track => {
      track.enabled = !newMuteState; // Mute/unmute
    });

    this.isMutedSubject.next(newMuteState);
    console.log(newMuteState ? 'Microphone Muted' : 'Microphone Unmuted');
  }*/

  toggleMicrophoneMute() {
    const currentState = this.isMutedSubject.getValue();
    const newMuteState = !currentState;

    this.microphoneService.toggleMute(newMuteState);
    this.isMutedSubject.next(newMuteState);
    console.log(newMuteState ? 'Microphone Muted' : 'Microphone Unmuted');
  }

  closeConversationalAgent() {
    this.cleanupResources();
    this.deepgramAgentWebSocketService.disconnect();
    // Emit event to parent component
    //this.cancelChatbot.emit();

    this.cancelChatbot.emit(this.tempHistory);
  }
  /** Store the message in tempHistory */
  private storeMessage(data: any) {
    const sender = data.role === 'assistant' ? 'bot' : data.role; // Convert "assistant" to "bot"

    const newMessage = new ChatMessage(
      this.tempHistory.length + 1,
      sender,
      data.content,
      'text',
      new Date().toLocaleTimeString(), // current time
      '',
      ''
    );

    this.tempHistory.push(newMessage);
    console.log('Message stored:', newMessage);
  }
}
